import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './AuthPages.css';

import { useDocumentTitle } from '../../hooks/useDocumentTitle';
export default function LoginPage() {
  useDocumentTitle('Connexion');

  const { login, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/mon-compte';
  // Vraie ou fausse selon que la visiteuse a été renvoyée ici depuis le tunnel
  // de commande (route protégée) ou qu'elle vient simplement se connecter.
  const depuisCommande = from === '/checkout';
  const [form, setForm]       = useState({ email: '', password: '' });
  const [erreur, setErreur]   = useState('');
  const [loading, setLoading] = useState(false);
  // La réinitialisation autonome par email est prévue en version 1.1 : en
  // attendant, la marche à suivre est indiquée directement à la cliente.
  const [aideOubli, setAideOubli] = useState(false);

  if (isLoggedIn) { navigate(from, { replace: true }); return null; }

  function handleChange(e) { setForm(f => ({ ...f, [e.target.name]: e.target.value })); setErreur(''); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.email || !form.password) { setErreur('Veuillez remplir tous les champs.'); return; }
    setLoading(true);
    try { await login(form.email, form.password); navigate(from, { replace: true }); }
    catch (err) { setErreur(err.message); }
    finally { setLoading(false); }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-card__header">
          <Link to="/" className="auth-logo"><span className="auth-logo__top">Les Bazars</span><span className="auth-logo__bottom">de Keassa</span></Link>
          <h1 className="auth-card__titre">Connexion</h1>
          <p className="auth-card__sous">Accédez à votre espace client</p>
        </div>
        {erreur && <div className="auth-erreur" role="alert">{erreur}</div>}
        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <div className="auth-field">
            <label htmlFor="email" className="auth-label">Adresse email</label>
            <input id="email" name="email" type="email" className="auth-input" placeholder="votre@email.com" value={form.email} onChange={handleChange} required />
          </div>
          <div className="auth-field">
            <div className="auth-label-row">
              <label htmlFor="password" className="auth-label">Mot de passe</label>
              <button
                type="button"
                className="auth-link-small"
                aria-expanded={aideOubli}
                aria-controls="aide-mot-de-passe"
                onClick={() => setAideOubli(o => !o)}
              >
                Mot de passe oublié ?
              </button>
            </div>
            <input id="password" name="password" type="password" className="auth-input" placeholder="••••••••" value={form.password} onChange={handleChange} required />
            {aideOubli && (
              <p id="aide-mot-de-passe" className="auth-aide" role="status">
                La réinitialisation automatique par email arrive prochainement.
                En attendant, écrivez-nous à{' '}
                <a href="mailto:contact@lesbazarsdekeassa.com">contact@lesbazarsdekeassa.com</a>{' '}
                ou depuis la page <Link to="/contact">Contact</Link> : nous vous
                réattribuons un accès sous 24 heures ouvrées.
              </p>
            )}
          </div>
          <button type="submit" className={`btn-or auth-submit${loading ? ' auth-submit--loading' : ''}`} disabled={loading}>
            {loading ? <><span className="auth-spinner" />Connexion...</> : 'Se connecter'}
          </button>
        </form>
        <p className="auth-footer-text">
          Pas encore de compte ?{' '}
          <Link to="/inscription" state={{ from: location.state?.from }} className="auth-link">Créer un compte</Link>
        </p>
        <div className="auth-divider"><span>ou</span></div>

        {/* Une visiteuse venue du tunnel de commande n'est jamais obligée de
            créer un compte : on lui rappelle qu'elle peut commander en invitée
            et on la renvoie à l'écran de choix. */}
        {depuisCommande ? (
          <>
            <Link to="/checkout" className="btn-outline auth-guest">
              Commander sans créer de compte
            </Link>
            <p className="auth-info">
              Le compte n'est pas obligatoire. Il vous permet simplement de
              retrouver vos adresses et l'historique de vos commandes. Votre
              panier est conservé dans tous les cas.
            </p>
          </>
        ) : (
          <Link to="/catalogue" className="btn-outline auth-guest">Continuer sans se connecter</Link>
        )}
      </div>
    </div>
  );
}